import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Image, X, Loader2, Globe, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import { resizeImage } from '@/lib/imageResize';

const API = API_BASE;

export default function CreatePostDialog({ open, onOpenChange, onPostCreated }) {
  const [content, setContent] = useState('');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [visibility, setVisibility] = useState('everyone');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length + files.length > 4) {
      toast.error('Maximum 4 images allowed');
      return;
    }

    const newPreviews = selectedFiles.map(file => URL.createObjectURL(file));
    setFiles(prev => [...prev, ...selectedFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(previews[index]);
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!content.trim() && files.length === 0) {
      toast.error('Add some content or an image');
      return;
    }

    setPosting(true);
    try {
      // Upload files first
      const mediaUrls = [];
      if (files.length > 0) {
        setUploading(true);
        for (const rawFile of files) {
          const file = await resizeImage(rawFile);
          const formData = new FormData();
          formData.append('file', file);
          const uploadResponse = await axios.post(`${API}/upload`, formData, {
            withCredentials: true
          });
          mediaUrls.push(uploadResponse.data.id);
        }
        setUploading(false);
      }

      // Create post
      const response = await axios.post(`${API}/posts`, {
        content: content.trim(),
        media_urls: mediaUrls,
        visibility,
      }, { withCredentials: true });

      onPostCreated(response.data);
      
      // Reset form
      setContent('');
      previews.forEach(url => URL.revokeObjectURL(url));
      setFiles([]);
      setPreviews([]);
      setVisibility('everyone');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setPosting(false);
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-3xl" data-testid="create-post-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-stone-900">Share a family moment</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="What everyday moment do you want to share with your family?"
            className="min-h-[120px] rounded-xl border-stone-200 resize-none focus:border-teal-500 focus:ring-teal-500"
            data-testid="post-content-input"
          />

          {/* Image Previews */}
          {previews.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {previews.map((url, index) => (
                <div key={index} className="relative rounded-xl overflow-hidden aspect-square">
                  <img src={url} alt="Preview" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeFile(index)}
                    className="absolute top-2 right-2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Visibility toggle */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="visibility-toggle">
            <button
              type="button"
              onClick={() => setVisibility('everyone')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                visibility === 'everyone'
                  ? 'bg-teal-50 border-teal-500 text-teal-700'
                  : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'
              }`}
              data-testid="visibility-everyone"
            >
              <Globe className="w-3.5 h-3.5" />
              Everyone in family
            </button>
            <button
              type="button"
              onClick={() => setVisibility('family_only')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                visibility === 'family_only'
                  ? 'bg-amber-50 border-amber-500 text-amber-700'
                  : 'bg-white border-stone-200 text-stone-600 hover:bg-stone-50'
              }`}
              data-testid="visibility-family-only"
            >
              <Lock className="w-3.5 h-3.5" />
              Core family only
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="text-stone-600 hover:text-teal-600 hover:bg-teal-50 rounded-full"
              disabled={files.length >= 4}
              data-testid="add-image-btn"
            >
              <Image className="w-5 h-5 mr-2" />
              Add Photos ({files.length}/4)
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={posting || (!content.trim() && files.length === 0)}
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
              data-testid="submit-post-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
              ) : posting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting...</>
              ) : (
                'Share'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
